import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { Spinner } from 'reactstrap'
import { Star, CheckCircle, AlertCircle, Clock, Send, ArrowRight } from 'react-feather'
import { getChatContactsFromServer } from './store/actions'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN)

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
})

const BASE = process.env.REACT_APP_BASEURL

// ─── Sub-components ───────────────────────────────────────────────────────────

const PillButton = ({ label, selected, onClick, disabled }) => {
  const isSelected = !!selected
  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '9px 20px',
        borderRadius: '50px',
        fontSize: '13.5px',
        fontWeight: isSelected ? '600' : '500',
        border: isSelected ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0',
        backgroundColor: isSelected ? '#6366f1' : '#f8fafc',
        color: isSelected ? '#ffffff' : '#334155',
        boxShadow: isSelected ? '0 4px 12px rgba(99, 102, 241, 0.3)' : '0 1px 2px rgba(0,0,0,0.02)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        margin: '4px 8px 4px 0',
        outline: 'none',
        userSelect: 'none'
      }}
      onMouseEnter={e => {
        if (!isSelected && !disabled) {
          e.currentTarget.style.borderColor = '#c7d2fe'
          e.currentTarget.style.backgroundColor = '#f5f3ff'
          e.currentTarget.style.color = '#4f46e5'
          e.currentTarget.style.transform = 'translateY(-1px)'
        }
      }}
      onMouseLeave={e => {
        if (!isSelected && !disabled) {
          e.currentTarget.style.borderColor = '#e2e8f0'
          e.currentTarget.style.backgroundColor = '#f8fafc'
          e.currentTarget.style.color = '#334155'
          e.currentTarget.style.transform = 'translateY(0)'
        }
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  )
}

const StarRating = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState(0)

  const getRatingLabel = (val) => {
    switch (val) {
      case 1: return '1 - Poor'
      case 2: return '2 - Fair'
      case 3: return '3 - Good'
      case 4: return '4 - Very Good'
      case 5: return '5 - Excellent'
      default: return 'Tap a star to rate'
    }
  }

  const activeVal = hovered || value

  return (
    <div className="my-1">
      <div className="d-flex align-items-center" style={{ gap: '8px' }}>
        {[1, 2, 3, 4, 5].map(n => (
          <Star
            key={n}
            size={32}
            fill={n <= activeVal ? '#f59e0b' : 'none'}
            stroke={n <= activeVal ? '#f59e0b' : '#cbd5e1'}
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'transform 0.18s ease',
              filter: n <= activeVal ? 'drop-shadow(0 2px 6px rgba(245, 158, 11, 0.35))' : 'none'
            }}
            onClick={() => !disabled && onChange(n)}
            onMouseEnter={e => {
              if (!disabled) {
                setHovered(n)
                e.currentTarget.style.transform = 'scale(1.18)'
              }
            }}
            onMouseLeave={e => {
              if (!disabled) {
                setHovered(0)
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          />
        ))}
      </div>
      <div className="mt-50">
        <span
          style={{
            fontSize: '12.5px',
            color: activeVal > 0 ? '#0f172a' : '#94a3b8',
            fontWeight: activeVal > 0 ? '600' : '400',
            background: activeVal > 0 ? '#fef3c7' : 'transparent',
            padding: activeVal > 0 ? '2px 8px' : '0',
            borderRadius: '12px',
            transition: 'all 0.2s ease'
          }}
        >
          {getRatingLabel(activeVal)}
        </span>
      </div>
    </div>
  )
}

// ─── Survey Steps Configuration ───────────────────────────────────────────────

const buildSteps = (brandName) => [
  {
    key: 'q1_received',
    question: 'Did your samples arrive?',
    type: 'pill',
    options: ['Yes', 'Still waiting', 'Never arrived']
  },
  {
    key: 'q2_tried',
    question: 'Did you get a chance to try it?',
    type: 'pill',
    options: ['Yes', 'Not yet']
  },
  {
    key: 'q3_rating',
    question: `How would you rate ${brandName} overall? Think about taste, nutrition profile, ingredient quality, and how you'd feel recommending it to clients.`,
    type: 'stars'
  },
  {
    key: 'q4_comment',
    question: 'What did you think? What stood out? Anything else you would like us to know?',
    type: 'text',
    optional: true
  },
  {
    key: 'q5_recommend',
    question: 'Would you recommend this product to your clients?',
    type: 'pill',
    options: ['Yes', 'Maybe', 'No']
  },
  {
    key: 'q6_work_again',
    question: `Would you be open to working with ${brandName} in future?`,
    type: 'pill',
    options: ['Yes definitely', 'Maybe', 'Not a fit for me']
  }
]

// ─── Main Component ────────────────────────────────────────────────────────────

const SampleSurveyCard = ({ message, participateRequestId }) => {
  const [loading, setLoading]           = useState(true)
  const [surveyData, setSurveyData]     = useState(null)
  const [step, setStep]                 = useState(0)
  const [answers, setAnswers]           = useState({})
  const [starValue, setStarValue]       = useState(0)
  const [textValue, setTextValue]       = useState('')
  const [submitting, setSubmitting]     = useState(false)
  const [finalMessage, setFinalMessage] = useState(null)
  const [isPaused, setIsPaused]         = useState(false)
  const [pausedResumeDate, setPausedResumeDate] = useState(null)
  const [done, setDone]                 = useState(false)

  const dispatch = useDispatch()
  const reqId = participateRequestId || message?.participate_request_id

  const loadSurvey = useCallback(() => {
    if (!reqId) {
      setLoading(false)
      return
    }

    axios.get(`${BASE}rd/sample-survey?participate_request_id=${reqId}`, authHeaders())
      .then(res => {
        const d = res.data
        if (!d.success) return

        setSurveyData(d)
        const currentAnswers = d.answers || {}
        setAnswers(currentAnswers)

        // 1. Flagged state (Only when permanently flagged or Never arrived)
        if (d.status === 'flagged' || currentAnswers.q1_received === 'Never arrived') {
          setFinalMessage('Thank you, the brand has been notified.')
          return
        }

        // 2. Completed state
        if (d.status === 'completed') {
          setDone(true)
          return
        }

        // 3. Paused state (Still waiting for samples OR Not yet tried)
        if (d.is_paused || d.status === 'paused') {
          setIsPaused(true)
          setPausedResumeDate(d.resume_at || 'in one week')
          return
        }

        // 4. Active step determination
        if (!currentAnswers.q1_received || currentAnswers.q1_received === 'Still waiting') {
          setStep(0)
        } else if (currentAnswers.q1_received === 'Yes' && (!currentAnswers.q2_tried || currentAnswers.q2_tried === 'Not yet')) {
          setStep(1)
        } else if (currentAnswers.q2_tried === 'Yes' && !currentAnswers.q3_rating) {
          setStep(2)
        } else if (currentAnswers.q3_rating && !currentAnswers.q4_comment) {
          setStep(3)
        } else if (currentAnswers.q4_comment && !currentAnswers.q5_recommend) {
          setStep(4)
        } else if (currentAnswers.q5_recommend && !currentAnswers.q6_work_again) {
          setStep(5)
        } else if (currentAnswers.q6_work_again) {
          setDone(true)
        }
      })
      .catch(() => { /* Stale fallback */ })
      .finally(() => setLoading(false))
  }, [reqId])

  useEffect(() => {
    loadSurvey()
  }, [loadSurvey])

  const steps = surveyData ? buildSteps(surveyData.brand_name || 'the brand') : []
  const currentStep = steps[step]

  const submitAnswer = useCallback(async (key, value) => {
    if (!reqId || submitting) return
    setSubmitting(true)

    try {
      const res = await axios.post(`${BASE}rd/sample-survey/answer`, {
        participate_request_id: reqId,
        question_key: key,
        answer_value: value
      }, authHeaders())

      const { action, message: serverMsg, resume_at: resDate } = res.data
      const updatedAnswers = { ...answers, [key]: value }
      setAnswers(updatedAnswers)

      if (action === 'stop') {
        setFinalMessage(serverMsg || 'Thank you, the brand has been notified.')
        dispatch(getChatContactsFromServer('inbox'))
        return
      }

      if (action === 'paused') {
        setIsPaused(true)
        setPausedResumeDate(resDate || 'in one week')
        return
      }

      const nextStep = step + 1
      setStarValue(0)
      setTextValue('')

      if (nextStep >= steps.length) {
        await axios.post(`${BASE}rd/sample-survey/complete`, {
          participate_request_id: reqId
        }, authHeaders())
        setDone(true)
        dispatch(getChatContactsFromServer('inbox'))
      } else {
        setStep(nextStep)
      }
    } catch {
      // Handle error
    } finally {
      setSubmitting(false)
    }
  }, [reqId, submitting, answers, step, steps.length, dispatch])

  // ── Render States ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="chat-survey-card font-inter" style={{ maxWidth: '440px' }}>
        <div className="d-flex align-items-center">
          <Spinner size="sm" color="primary" className="mr-75" />
          <span style={{ fontSize: '13px', color: '#64748b' }}>Loading feedback questionnaire...</span>
        </div>
      </div>
    )
  }

  if (!surveyData) {
    return (
      <div className="chat-content">
        <div dangerouslySetInnerHTML={{ __html: message?.message || '' }} />
      </div>
    )
  }

  // ── 1. Flagged State (Delivery issue reported) ──────────────────────────────
  if (finalMessage) {
    return (
      <div
        className="chat-survey-card font-inter"
        style={{
          maxWidth: '440px',
          background: '#fffbf0 !important',
          borderColor: '#fde68a !important'
        }}
      >
        <div className="d-flex align-items-start">
          <AlertCircle size={22} className="text-warning mr-75 flex-shrink-0 mt-25" />
          <div>
            <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
              Sample Delivery Alert
            </h6>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: 0, lineHeight: '1.45' }}>
              {finalMessage}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── 2. Paused State (Still waiting for delivery OR Not yet tried) ──────────
  if (isPaused) {
    const isDeliveryPause = answers.q1_received === 'Still waiting'
    return (
      <div className="chat-survey-card font-inter" style={{ maxWidth: '440px' }}>
        <div className="d-flex align-items-start">
          <Clock size={22} className="text-primary mr-75 flex-shrink-0 mt-25" />
          <div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: '#6366f1',
                background: '#eef2ff',
                padding: '2px 8px',
                borderRadius: '12px',
                display: 'inline-block',
                marginBottom: '6px'
              }}
            >
              {isDeliveryPause ? 'Delivery Check Paused' : 'Feedback Paused'}
            </span>
            <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>
              {isDeliveryPause ? 'Thank you, the brand has been notified.' : 'Take your time to try the samples!'}
            </h6>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', lineHeight: '1.45' }}>
              {isDeliveryPause
                ? `We have notified the brand and will check back with you in 7 days (around ${pausedResumeDate || 'in 7 days'}) to see if your samples have arrived.`
                : `No problem at all. We will check back with you around ${pausedResumeDate || 'in one week'} once you’ve had a chance to test the products.`
              }
            </p>
            <small style={{ fontSize: '11.5px', color: '#94a3b8' }}>
              {surveyData.brand_name} &bull; {surveyData.campaign_name}
            </small>
          </div>
        </div>
      </div>
    )
  }

  // ── 3. Completed State: Simple Clean Thank You Card ─────────────────────────
  if (done) {
    return (
      <div className="chat-survey-card font-inter" style={{ maxWidth: '440px' }}>
        <div className="d-flex align-items-start">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center mr-75 flex-shrink-0"
            style={{ width: '36px', height: '36px', background: '#dcfce7', color: '#16a34a' }}
          >
            <CheckCircle size={20} />
          </div>
          <div>
            <h6 style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a', marginBottom: '3px' }}>
              Thank you for your feedback!
            </h6>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: 0, lineHeight: '1.45' }}>
              Your response has been shared with <strong>{surveyData.brand_name || 'the brand'}</strong>.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── 4. Active Question Step ────────────────────────────────────────────────
  if (!currentStep) return null

  const progressPct = Math.round(((step + 1) / steps.length) * 100)

  return (
    <div className="chat-survey-card font-inter" style={{ maxWidth: '450px' }}>
      {/* Top Header: Badge & Step Indicator */}
      <div className="d-flex align-items-center justify-content-between mb-75">
        <span
          className="d-inline-flex align-items-center"
          style={{
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: '#6366f1',
            background: '#eef2ff',
            padding: '3px 10px',
            borderRadius: '12px'
          }}
        >
          <Star size={11} className="mr-50" fill="#6366f1" />
          Sample Feedback
        </span>
        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
          Step {step + 1} of {steps.length}
        </span>
      </div>

      {/* Thin Smooth Progress Bar */}
      <div
        style={{
          height: '3px',
          backgroundColor: '#f1f5f9',
          borderRadius: '3px',
          marginBottom: '14px',
          overflow: 'hidden'
        }}
      >
        <div
          role="progressbar"
          style={{
            height: '100%',
            width: `${progressPct}%`,
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
            borderRadius: '3px',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </div>

      {/* Step 0 Friendly Intro Text */}
      {step === 0 && (
        <div
          style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #f1f5f9',
            borderRadius: '10px',
            padding: '10px 14px',
            marginBottom: '14px'
          }}
        >
          <p
            style={{
              fontSize: '13px',
              color: '#475569',
              marginBottom: 0,
              lineHeight: '1.5'
            }}
          >
            You recently requested samples from <strong>{surveyData.brand_name || 'this brand'}</strong> &mdash; got a minute to share what you thought? It only takes 2 minutes and helps the brand improve.
          </p>
        </div>
      )}

      {/* Question Title */}
      <h6
        style={{
          fontSize: '15px',
          fontWeight: '600',
          color: '#0f172a',
          lineHeight: '1.45',
          marginBottom: '14px'
        }}
      >
        {currentStep.question}
      </h6>

      {/* Pill Options */}
      {currentStep.type === 'pill' && (
        <div className="d-flex flex-wrap align-items-center">
          {currentStep.options.map(opt => (
            <PillButton
              key={opt}
              label={opt}
              selected={answers[currentStep.key] === opt}
              disabled={submitting}
              onClick={() => submitAnswer(currentStep.key, opt)}
            />
          ))}
          {submitting && <Spinner size="sm" color="primary" className="ml-50" />}
        </div>
      )}

      {/* Star Rating Option */}
      {currentStep.type === 'stars' && (
        <div>
          <StarRating
            value={starValue}
            onChange={setStarValue}
            disabled={submitting}
          />
          {starValue > 0 && (
            <div className="mt-1">
              <button
                type="button"
                className="btn btn-primary btn-sm px-2 font-inter font-weight-bold d-inline-flex align-items-center"
                style={{
                  borderRadius: '50px',
                  backgroundColor: '#6366f1',
                  borderColor: '#6366f1',
                  padding: '8px 20px',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                }}
                disabled={submitting}
                onClick={() => submitAnswer(currentStep.key, String(starValue))}
              >
                {submitting ? <Spinner size="sm" className="mr-50" /> : <ArrowRight size={14} className="mr-50" />}
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Open Text Comment Option */}
      {currentStep.type === 'text' && (
        <div className="mt-50">
          <textarea
            rows={3}
            className="form-control font-inter font-small-3 mb-1"
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
            placeholder="What did you think? What stood out? Anything else you'd like to share..."
            style={{
              borderRadius: '10px',
              resize: 'vertical',
              border: '1.5px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              padding: '10px 14px',
              fontSize: '13.5px',
              lineHeight: '1.5'
            }}
            disabled={submitting}
          />
          <div className="d-flex align-items-center" style={{ gap: '14px' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm font-inter font-weight-bold d-inline-flex align-items-center"
              style={{
                borderRadius: '50px',
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
                padding: '8px 22px',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
              }}
              disabled={submitting}
              onClick={() => submitAnswer(currentStep.key, textValue.trim() || '(skipped)')}
            >
              {submitting ? <Spinner size="sm" className="mr-50" /> : <Send size={13} className="mr-50" />}
              Submit
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm text-muted p-0 font-inter"
              style={{ fontSize: '13px', textDecoration: 'none' }}
              disabled={submitting}
              onClick={() => submitAnswer(currentStep.key, '(skipped)')}
            >
              Skip this step
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SampleSurveyCard
